import React from 'react'
import { Outlet, Link, NavLink, useLocation } from 'react-router-dom'
import { clearAuthSession } from '../api'
import {
  ANY_AUTHENTICATED_ROLES,
  MANAGER_ROLES,
  TESTER_OR_ADMIN_ROLES,
  getRealm,
  getRealmUrl,
  getStoredAuthRole,
  isAdminRole,
} from '../lib/realm'
import { useI18n } from '../i18n'
import { Select } from './ui'
import fanltvLogo from '../assets/fanltv-logo.svg'
import '../styles/components/layout.css'

const THEME_KEY = 'admin_theme'

export default function Layout() {
  const { lang, t, setLang } = useI18n()
  const location = useLocation()
  const pathname = location.pathname
  const currentRealm = getRealm()
  const currentRole = getStoredAuthRole()
  const isLogsPath = pathname.includes('/logs')
  const isAutotestsPath = pathname.includes('/autotests')

  const [logsExpanded, setLogsExpanded] = React.useState(isLogsPath)
  const [autotestsExpanded, setAutotestsExpanded] = React.useState(isAutotestsPath)

  React.useEffect(() => {
    if (isLogsPath) setLogsExpanded(true)
  }, [isLogsPath])

  React.useEffect(() => {
    if (isAutotestsPath) setAutotestsExpanded(true)
  }, [isAutotestsPath])
  function getInitialTheme() {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === 'light' || saved === 'dark') return saved
    return 'dark'
  }

  function applyTheme(theme) {
    document.documentElement.dataset.theme = theme
  }

  const [theme, setTheme] = React.useState(getInitialTheme)

  React.useLayoutEffect(() => {
    applyTheme(theme)
    localStorage.setItem(THEME_KEY, theme)
  }, [theme])

  function handleLogout() {
    clearAuthSession()
    window.location.href = getRealmUrl(currentRealm, '/login')
  }

  function toggleTheme() {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
  }

  function handleLangChange(e) {
    const value = e.target.value
    if (value === 'ru' || value === 'en') setLang(value)
  }

  const showModels = MANAGER_ROLES.includes(currentRole)
  const showFyp = MANAGER_ROLES.includes(currentRole)
  const showFans = ANY_AUTHENTICATED_ROLES.includes(currentRole)
  const showChats = ANY_AUTHENTICATED_ROLES.includes(currentRole)
  const showPurchases = ANY_AUTHENTICATED_ROLES.includes(currentRole)
  const showAnalytics = TESTER_OR_ADMIN_ROLES.includes(currentRole)
  const showLogs = TESTER_OR_ADMIN_ROLES.includes(currentRole)
  const showAutotests = TESTER_OR_ADMIN_ROLES.includes(currentRole)
  const showSettings = isAdminRole(currentRole)
  const showAgencySettings = currentRole === 'agency'

  return (
    <div className="admin-layout">
      <header className="admin-header">
        <div className="admin-header-left">
          <Link to="/old" className="admin-brand" aria-label="FanLTV classic home">
            <img src={fanltvLogo} alt="" aria-hidden="true" />
            <span>FanLTV</span>
          </Link>
          <Link to="/v2" className="admin-beta-link">V2</Link>
        </div>
        <div className="admin-header-right">
          <Select
            className="header-lang-select"
            value={lang}
            onChange={(v) => setLang(v)}
            options={[
              { value: 'ru', label: t('layout.language.ruShort') },
              { value: 'en', label: t('layout.language.enShort') },
            ]}
            placeholder={t('layout.language')}
            dropdownPosition="down"
          />
          <button
            type="button"
            className={`header-icon header-icon-theme header-icon-theme--${theme === 'dark' ? 'sun' : 'moon'}`}
            onClick={toggleTheme}
            title={theme === 'dark' ? t('layout.theme.light') : t('layout.theme.dark')}
            aria-label={theme === 'dark' ? t('layout.theme.light') : t('layout.theme.dark')}
          >
            {theme === 'dark' ? (
              <svg className="header-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg className="header-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="header-icon header-icon-logout"
            onClick={handleLogout}
            title={t('layout.logout')}
            aria-label={t('layout.logout')}
          >
            <svg className="header-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      <div className="admin-body">
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            {showModels && (
              <NavLink
                to="models"
                end
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.models')}
              </NavLink>
            )}
            {showFyp && (
              <NavLink
                to="fyp"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.fyp')}
              </NavLink>
            )}
            {showFans && (
              <NavLink
                to="fans"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.fans')}
              </NavLink>
            )}
            {showChats && (
              <NavLink
                to="chats"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.chats')}
              </NavLink>
            )}
            {showAnalytics && (
              <NavLink
                to="analytics"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.analytics')}
              </NavLink>
            )}
            {showPurchases && (
              <NavLink
                to="purchases"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.purchases')}
              </NavLink>
            )}
            {showSettings && (
              <NavLink
                to="users"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('settings.nav.users')}
              </NavLink>
            )}
            {showLogs && (
              <div className="admin-nav-group">
                <div
                  className={`admin-nav-parent ${isLogsPath ? 'active' : ''}`}
                  onClickCapture={(e) => {
                    if (isLogsPath && logsExpanded) {
                      e.preventDefault()
                      e.stopPropagation()
                      setLogsExpanded(false)
                    } else if (!logsExpanded) {
                      setLogsExpanded(true)
                    }
                  }}
                >
                  <NavLink
                    to="logs"
                    className={({ isActive }) => `admin-nav-link admin-nav-link--parent ${isActive || isLogsPath ? 'active' : ''}`}
                  >
                    {t('layout.nav.logs')}
                  </NavLink>
                  <span className={`admin-nav-chevron ${logsExpanded ? 'expanded' : ''}`} aria-hidden>
                    ▸
                  </span>
                </div>
                {logsExpanded && (
                  <nav className="admin-nav admin-nav--sub settings-subnav">
                    <NavLink
                      to="logs"
                      end
                      className={({ isActive }) => `admin-nav-link admin-nav-link--sub ${isActive ? 'active' : ''}`}
                    >
                      {t('logs.nav.graphs')}
                    </NavLink>
                    <NavLink
                      to="logs/proactive"
                      className={({ isActive }) => `admin-nav-link admin-nav-link--sub ${isActive ? 'active' : ''}`}
                    >
                      {t('logs.nav.proactive')}
                    </NavLink>
                  </nav>
                )}
              </div>
            )}
            {showAutotests && (
              <div className="admin-nav-group">
                <div
                  className={`admin-nav-parent ${isAutotestsPath ? 'active' : ''}`}
                  onClickCapture={(e) => {
                    if (isAutotestsPath && autotestsExpanded) {
                      e.preventDefault()
                      e.stopPropagation()
                      setAutotestsExpanded(false)
                    } else if (!autotestsExpanded) {
                      setAutotestsExpanded(true)
                    }
                  }}
                >
                  <NavLink
                    to="autotests"
                    className={({ isActive }) => `admin-nav-link admin-nav-link--parent ${isActive || isAutotestsPath ? 'active' : ''}`}
                  >
                    {t('autotests.nav.title')}
                  </NavLink>
                  <span className={`admin-nav-chevron ${autotestsExpanded ? 'expanded' : ''}`} aria-hidden>
                    ▸
                  </span>
                </div>
                {autotestsExpanded && (
                  <nav className="admin-nav admin-nav--sub settings-subnav">
                    <NavLink
                      to="autotests"
                      end
                      className={({ isActive }) => `admin-nav-link admin-nav-link--sub ${isActive ? 'active' : ''}`}
                    >
                      {t('autotests.nav.dashboard')}
                    </NavLink>
                    <NavLink
                      to="autotests/analysis"
                      className={({ isActive }) => `admin-nav-link admin-nav-link--sub ${isActive ? 'active' : ''}`}
                    >
                      {t('autotests.nav.analysis')}
                    </NavLink>
                    <NavLink
                      to="autotests/prompts"
                      className={({ isActive }) => `admin-nav-link admin-nav-link--sub ${isActive ? 'active' : ''}`}
                    >
                      Промпты
                    </NavLink>
                  </nav>
                )}
              </div>
            )}
            {showSettings && (
              <NavLink
                to="settings/configuration"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.configuration')}
              </NavLink>
            )}
            {showAgencySettings && (
              <NavLink
                to="agency-settings"
                className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
              >
                {t('layout.nav.agencySettings')}
              </NavLink>
            )}
            {(showAgencySettings || showSettings) && (
              <NavLink to="traffic-intelligence" className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}>
                Traffic Intelligence
              </NavLink>
            )}
          </nav>
        </aside>
        <main className="admin-content">
          <div className="page-wrapper">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
